import type { RouteRecordRaw } from "vue-router"
import type { OrganizationScopeType } from "@/types/organization-scope"
import { markRaw } from "vue"
import { HQ_FEATURE_READINESS } from "@/features/hq/hqFeatureReadiness"
import { MEMBER_RISK_AVAILABLE } from "@/features/memberRiskAvailability"
import { injectRouteList } from "@/hooks/useRouter"
import LevelTwoRouter from "@/layout/components/NavigationBar/LevelTwoRouter.vue"
import { CHAIN_ACTION_PERMISSIONS, HQ_ACTION_PERMISSIONS, HQ_ROOT_ACTION_PERMISSIONS, type PermissionRequirement } from "@/utils/roleFeatureAccess"

/**
 * 总部专属路由。
 *
 * 路径前缀 /hq/* — 单店账号 router.beforeEach 拦截重定向。
 * 菜单可见性:meta.permissions 走现有 hasPermission 过滤。
 *
 * 2026-07-16 菜单重构(总部 IA v2):七大一级 = 首页(经营驾驶舱)/ 报表中心 / 营销中心 /
 * 财务中心 / 预订中心 / 采购中心 / 运营中心。⚠️ 既有页面的叶子路由路径全部保持不变(页面间跳转零破坏),
 * 只重排分组树;未建设板块用 ComingSoon 占位立结构。旧一级路径保留 legacy redirect。
 */

const Layout = () => import("@/layout/index.vue")
const StoredValueRules = () => import("@/views/page/hq/StoredValueRules.vue")
const CashbackRules = () => import("@/views/page/hq/CashbackRules.vue")
const ReferralCashbackCampaigns = () => import("@/views/page/hq/ReferralCashbackCampaigns.vue")
const CouponTemplates = () => import("@/views/page/hq/CouponTemplates.vue")
const CouponRules = () => import("@/views/page/hq/CouponRules.vue")
const ManualBenefits = () => import("@/views/page/hq/ManualBenefits.vue")
const AssetPolicy = () => import("@/views/page/hq/AssetPolicy.vue")
const BrandFacade = () => import("@/views/page/hq/BrandFacade.vue")
const BrandHome = () => import("@/views/page/hq/BrandHome.vue")
const ComingSoon = () => import("@/views/page/hq/ComingSoon.vue")
const Stores = () => import("@/views/page/hq/Stores.vue")
const StoreGroups = () => import("@/views/page/hq/StoreGroups.vue")
const Regions = () => import("@/views/page/hq/Regions.vue")
const ChainEmployeeBindings = () => import("@/views/page/hq/ChainEmployeeBindings.vue")
const Members = () => import("@/views/page/hq/HqMembers.vue")
const MemberImport = () => import("@/views/page/hq/member-import/MemberImportPage.vue")
const MemberOverview = () => import("@/views/page/hq/MemberOverview.vue")
const CrmInsights = () => import("@/views/page/hq/CrmInsights.vue")
const MemberMigration = () => import("@/views/page/hq/MemberMigration.vue")
const MemberDedupe = () => import("@/views/page/hq/MemberDedupe.vue")
const MemberTags = () => import("@/views/page/hq/MemberTags.vue")
const MemberSegments = () => import("@/views/page/hq/MemberSegments.vue")
const MessagingCampaigns = () => import("@/views/page/hq/MessagingCampaigns.vue")
const OverrideAuthorization = () => import("@/views/page/hq/OverrideAuthorization.vue")
const MemberLevels = () => import("@/views/page/hq/MemberLevels.vue")
const MemberAssets = () => import("@/views/page/hq/MemberAssets.vue")
const BrandPointRules = () => import("@/views/page/hq/brand-points/BrandPointRules.vue")
const BrandPointMigrationPreview = () => import("@/views/page/hq/brand-points/BrandPointMigrationPreview.vue")
const BrandPointRecoveries = () => import("@/views/page/hq/brand-points/BrandPointRecoveries.vue")
const BrandPointReports = () => import("@/views/page/hq/brand-points/BrandPointReports.vue")
const BrandPointMall = () => import("@/views/page/hq/brand-points/BrandPointMall.vue")
const StoredValueMemberLedger = () =>
  import("@/views/page/hq/finance/member-stored-value/MemberLedger.vue")
const CrossStoreConsumptionDetail = () =>
  import("@/views/page/hq/finance/member-stored-value/CrossStoreConsumptionDetail.vue")
const StoredValueRechargeConsumptionStatistics = () =>
  import("@/views/page/hq/finance/member-stored-value/StoredValueStatisticsPage.vue")
const StoredValueConsumptionTrace = () =>
  import("@/views/page/hq/finance/member-stored-value/ConsumptionTrace.vue")
const IncomeExpenseOverview = () => import("@/views/page/hq/finance/income-expense/pages/Overview.vue")
const MonthlyExpenses = () => import("@/views/page/hq/finance/income-expense/pages/MonthlyExpenses.vue")
const IncomeExpenseLedger = () => import("@/views/page/hq/finance/income-expense/pages/Ledger.vue")
const ExpenseCategories = () => import("@/views/page/hq/finance/income-expense/pages/ExpenseCategories.vue")
const ReservationDashboardSummary = () => import("@/views/page/hq/ReservationDashboardSummary.vue")
const ReservationOrderDetails = () => import("@/views/page/hq/ReservationOrderDetails.vue")
const ReservationRules = () => import("@/views/page/hq/ReservationRules.vue")
const Orders = () => import("@/views/page/hq/Orders.vue")
const MemberDetail = () => import("@/views/page/membership/components/MemberManageDetail.vue")
const BusinessOverview = () => import("@/views/page/hq/business/BusinessOverview.vue")
const HqHandoverBusinessSummary = () =>
  import("@/views/page/dashboard/components/HandoverStatement/HandoverBusinessSummary.vue")
const HqHandoverBusinessDaily = () =>
  import("@/views/page/dashboard/components/HandoverStatement/HandoverBusinessDaily.vue")
const HqHandoverBusinessBillDetail = () =>
  import("@/views/page/dashboard/components/HandoverStatement/HandoverBusinessBillDetail.vue")
const HqHandoverBusinessProduct = () =>
  import("@/views/page/dashboard/components/HandoverStatement/HandoverBusinessProduct.vue")
const HqHandoverBusinessTable = () =>
  import("@/views/page/dashboard/components/HandoverStatement/HandoverBusinessTable.vue")
const OperationTargets = () => import("@/views/page/hq/OperationTargets.vue")
const ProductAnalysis = () => import("@/views/page/hq/ProductAnalysis.vue")
const MemberAnalysis = () => import("@/views/page/hq/MemberAnalysis.vue")
const TableAnalysis = () => import("@/views/page/hq/TableAnalysis.vue")
const PaymentAnalysis = () => import("@/views/page/hq/PaymentAnalysis.vue")
const IndicatorCompare = () => import("@/views/page/hq/IndicatorCompare.vue")
const OperatingStatistics = () => import("@/views/page/hq/OperatingStatistics.vue")
const MarketingEffect = () => import("@/views/page/hq/MarketingEffect.vue")
const StaffPerformance = () => import("@/views/page/hq/StaffPerformance.vue")
const StoreSnapshot = () => import("@/views/page/hq/StoreSnapshot.vue")
const CustomReports = () => import("@/views/page/hq/CustomReports.vue")
const ReportCenterHome = () => import("@/views/page/hq/ReportCenterHome.vue")
const DataScreen = () => import("@/views/page/hq/DataScreen.vue")
const OpsAlertsOverview = () => import("@/views/page/hq/OpsAlertsOverview.vue")
const OpsAlertsList = () => import("@/views/page/hq/OpsAlertsList.vue")
const OpsAlertsRules = () => import("@/views/page/hq/OpsAlertsRules.vue")
const ProductInventory = () => import("@/views/page/hq/product-inventory/ProductInventory.vue")

const HQ_PATHS = {
  homeMenu: "/hq/home/main",
  homeTab: "/hq/home/main/home",
  brandFacadeMenu: "/hq/brand-building/brand-facade",
  brandFacadeTab: "/hq/brand-building/brand-facade/brand-facade",
  storesMenu: "/hq/organization/stores",
  storesTab: "/hq/organization/stores/stores",
  storeGroupsMenu: "/hq/organization/store-groups",
  storeGroupsTab: "/hq/organization/store-groups/store-groups",
  regionsMenu: "/hq/organization/regions",
  regionsTab: "/hq/organization/regions/regions",
  employeeBindingsMenu: "/hq/organization/employee-bindings",
  employeeBindingsTab: "/hq/organization/employee-bindings/employee-bindings",
  // 营销中心(原会员中心;叶子路径不变)
  marketingHomeMenu: "/hq/member-management/home",
  membersMenu: "/hq/member-management/members",
  memberOverviewTab: "/hq/member-management/members/overview",
  crmInsightsTab: "/hq/member-management/members/insights",
  membersListTab: "/hq/member-management/members/list",
  memberImportTab: "/hq/member-management/members/import",
  memberMigrationTab: "/hq/member-management/members/member-migration",
  memberDedupeTab: "/hq/member-management/members/dedupe",
  memberTagsTab: "/hq/member-management/members/tags",
  memberLevelsMenu: "/hq/member-management/member-levels",
  memberLevelsTab: "/hq/member-management/member-levels/member-levels",
  storedValueMenu: "/hq/member-marketing/stored-value-rules",
  storedValueTab: "/hq/member-marketing/stored-value-rules/stored-value-rules",
  memberSegmentsTab: "/hq/member-marketing/stored-value-rules/member-segments",
  overrideAuthorizationTab: "/hq/member-marketing/stored-value-rules/override-authorization",
  cashbackMenu: "/hq/member-marketing/cashback-rules",
  cashbackTab: "/hq/member-marketing/cashback-rules/cashback-rules",
  referralCashbackMenu: "/hq/member-marketing/referral-cashback",
  referralCashbackTab: "/hq/member-marketing/referral-cashback/campaigns",
  couponTemplatesMenu: "/hq/member-marketing/coupon-templates",
  couponTemplatesTab: "/hq/member-marketing/coupon-templates/coupon-templates",
  couponRulesMenu: "/hq/member-marketing/coupon-rules",
  couponRulesTab: "/hq/member-marketing/coupon-rules/coupon-rules",
  manualBenefitsMenu: "/hq/member-marketing/manual-benefits",
  manualBenefitsTab: "/hq/member-marketing/manual-benefits/manual-benefits",
  brandPointsMenu: "/hq/member-marketing/brand-points",
  brandPointRulesTab: "/hq/member-marketing/brand-points/rules",
  brandPointMigrationPreviewTab: "/hq/member-marketing/brand-points/migration-preview",
  brandPointRecoveriesTab: "/hq/member-marketing/brand-points/recoveries",
  brandPointReportsTab: "/hq/member-marketing/brand-points/reports",
  brandPointMallTab: "/hq/member-marketing/brand-points/mall",
  marketingSmsMenu: "/hq/member-management/sms",
  marketingSmsTab: "/hq/member-management/sms/sms",
  messagingMenu: "/hq/member-marketing/messaging",
  messagingTab: "/hq/member-marketing/messaging/messaging",
  memberAssetsMenu: "/hq/member-assets/assets-dashboard",
  memberAssetsTab: "/hq/member-assets/assets-dashboard/assets-dashboard",
  assetPolicyTab: "/hq/member-assets/assets-dashboard/asset-policy",
  marketingEffectTab: "/hq/member-assets/marketing-effect",
  ordersMenu: "/hq/member-assets/orders",
  ordersTab: "/hq/member-assets/orders/orders",
  memberStoredValueMenu: "/hq/finance/member-stored-value",
  memberStoredValueSettlementTab: "/hq/finance/member-stored-value/settlement",
  memberStoredValueRechargeConsumptionStatisticsTab: "/hq/finance/member-stored-value/recharge-consumption-statistics",
  memberStoredValueRechargeStatisticsTab: "/hq/finance/member-stored-value/recharge-statistics",
  memberStoredValueConsumptionStatisticsTab: "/hq/finance/member-stored-value/consumption-statistics",
  memberStoredValueLedgerTab: "/hq/finance/member-stored-value/member-ledger",
  memberStoredValueConsumptionTraceTab: "/hq/finance/member-stored-value/consumption-trace",
  incomeExpenseMenu: "/hq/finance/income-expense",
  incomeExpenseOverviewTab: "/hq/finance/income-expense/overview",
  incomeExpenseMonthlyTab: "/hq/finance/income-expense/monthly-expenses",
  incomeExpenseLedgerTab: "/hq/finance/income-expense/ledger",
  incomeExpenseCategoriesTab: "/hq/finance/income-expense/expense-categories",
  reservationDashboardMenu: "/hq/reservation-center/dashboard",
  reservationSummaryTab: "/hq/reservation-center/dashboard/summary",
  reservationOrderDetailsTab: "/hq/reservation-center/dashboard/order-details",
  reservationConfigMenu: "/hq/reservation-center/config",
  reservationBasicRulesTab: "/hq/reservation-center/config/basic-rules",
  reservationRoomSettingsTab: "/hq/reservation-center/config/room-settings",
  procurementCenterMenu: "/hq/procurement-center/main",
  procurementCenterTab: "/hq/procurement-center/main/index",
  riskWarningMenu: "/hq/member-management/risk-warning",
  riskOverviewTab: "/hq/member-management/risk-warning/overview",
  riskCustomerListTab: "/hq/member-management/risk-warning/customer-list",
  riskStoreDispatchTab: "/hq/member-management/risk-warning/store-dispatch",
  riskEliminationRecordsTab: "/hq/member-management/risk-warning/elimination-records",
  riskRuleConfigTab: "/hq/member-management/risk-warning/rule-config",
  reservationRulesMenu: "/hq/member-marketing/reservation-rules",
  reservationRulesTab: "/hq/member-marketing/reservation-rules/reservation-rules",
  // 报表中心
  reportHomeMenu: "/hq/business-reports/report-home",
  reportHomeTab: "/hq/business-reports/report-home/report-home",
  businessStatisticsMenu: "/hq/business-reports/business-statistics",
  businessOverviewTab: "/hq/business-reports/business-statistics/overview",
  handoverReportMenu: "/hq/business-reports/handover-report",
  handoverSummaryTab: "/hq/business-reports/handover-report/summary",
  handoverDailyTab: "/hq/business-reports/handover-report/daily",
  handoverBillDetailTab: "/hq/business-reports/handover-report/bill-detail",
  handoverProductStatisticsTab: "/hq/business-reports/handover-report/product",
  handoverTableStatisticsTab: "/hq/business-reports/handover-report/table",
  tableAnalysisTab: "/hq/business-reports/business-statistics/table-analysis",
  paymentAnalysisTab: "/hq/business-reports/business-statistics/payment-analysis",
  operationTargetsMenu: "/hq/business-reports/operation-targets",
  operationTargetsTab: "/hq/business-reports/operation-targets/operation-targets",
  productAnalysisMenu: "/hq/business-reports/product-analysis",
  productAnalysisTab: "/hq/business-reports/product-analysis/product-analysis",
  memberAnalysisTab: "/hq/business-reports/business-statistics/member-analysis",
  operatingReportsMenu: "/hq/business-reports/operating-reports",
  orderStatsTab: "/hq/business-reports/operating-reports/order-stats",
  revenueDiscountTab: "/hq/business-reports/operating-reports/revenue-discount",
  comprehensiveStatsTab: "/hq/business-reports/operating-reports/comprehensive-stats",
  indicatorCompareTab: "/hq/business-reports/operating-reports/indicator-compare",
  tableReportsMenu: "/hq/business-reports/table-reports",
  tableReportsTab: "/hq/business-reports/table-reports/table-reports",
  productReportsMenu: "/hq/business-reports/product-reports",
  productReportsTab: "/hq/business-reports/product-reports/product-reports",
  paymentReportsMenu: "/hq/business-reports/payment-reports",
  paymentReportsTab: "/hq/business-reports/payment-reports/payment-reports",
  financeReportsMenu: "/hq/business-reports/finance-reports",
  financeReportsTab: "/hq/business-reports/finance-reports/finance-reports",
  staffPerformanceMenu: "/hq/business-reports/staff-performance",
  staffPerformanceTab: "/hq/business-reports/staff-performance/staff-performance",
  storeSnapshotTab: "/hq/business-reports/staff-performance/store-snapshot",
  customReportsMenu: "/hq/business-reports/custom-reports",
  customReportsTab: "/hq/business-reports/custom-reports/custom-reports",
  dataScreenMenu: "/hq/business-reports/data-screen",
  dataScreenTab: "/hq/business-reports/data-screen/data-screen",
  opsAlertsMenu: "/hq/ops-alerts/main",
  opsAlertsOverviewTab: "/hq/ops-alerts/main/overview",
  opsAlertsListTab: "/hq/ops-alerts/main/list",
  opsAlertsRulesTab: "/hq/ops-alerts/main/rules",
  // 采购中心
  supplierMenu: "/hq/procurement-center/suppliers",
  supplierTab: "/hq/procurement-center/suppliers/suppliers",
  expiryMenu: "/hq/procurement-center/expiry",
  expiryTab: "/hq/procurement-center/expiry/expiry",
  // 运营中心
  productManageMenu: "/hq/operations-center/products",
  productManageTab: "/hq/operations-center/products/products",
  reservationManageMenu: "/hq/operations-center/reservation",
  productInventoryMenu: "/hq/operations-center/product-inventory",
  productInventoryLegacyTab: "/hq/operations-center/product-inventory/product-inventory",
  productOverviewTab: "/hq/operations-center/product-inventory/overview",
  headquartersProductsTab: "/hq/operations-center/product-inventory/products",
  headquartersProductAnalysisTab: "/hq/operations-center/product-inventory/product-analysis",
} as const

const toPermissionArray = (permissions: PermissionRequirement) =>
  Array.isArray(permissions) ? permissions : [permissions]

const mergePermissions = (...requirements: PermissionRequirement[]) =>
  Array.from(new Set(requirements.flatMap(toPermissionArray)))

const HEADQUARTER_SCOPE_TYPES: OrganizationScopeType[] = ["chain-headquarter"]
const AGGREGATE_SCOPE_TYPES: OrganizationScopeType[] = ["chain-headquarter", "chain-region"]
// 本地 vite dev 与 test/staging 可查看全部总部页面；production 继续执行正式 readiness 门禁。
const LOCAL_PREVIEW_ROUTES_ENABLED = HQ_FEATURE_READINESS.previewEnabled
const MEMBER_TAG_API_READY = HQ_FEATURE_READINESS.memberTagApiReady
const MEMBER_TAG_ROUTE_REGISTERED = MEMBER_TAG_API_READY || LOCAL_PREVIEW_ROUTES_ENABLED
const referralCashbackViewPermissions = (
  HQ_FEATURE_READINESS.referralCashbackUsePreview
  || HQ_FEATURE_READINESS.referralCashbackTestApiMode
)
  ? mergePermissions(
      HQ_ACTION_PERMISSIONS.viewReferralCashbackActivities,
      HQ_ACTION_PERMISSIONS.manageReferralCashbackActivities,
      HQ_ACTION_PERMISSIONS.manageCashbackRules,
    )
  : mergePermissions(
      HQ_ACTION_PERMISSIONS.viewReferralCashbackActivities,
      HQ_ACTION_PERMISSIONS.manageReferralCashbackActivities,
    )
const storedValueReportPermissions = HQ_ACTION_PERMISSIONS.viewBusinessReports
const financePermissions = mergePermissions(
  HQ_ACTION_PERMISSIONS.viewSettlement,
  HQ_ACTION_PERMISSIONS.manageSettlement,
)
const financeRootPermissions = mergePermissions(storedValueReportPermissions, financePermissions)

type HqRouteComponent = NonNullable<RouteRecordRaw["component"]>
type HqRouteRedirect = Exclude<RouteRecordRaw["redirect"], undefined>

interface HqTabPage {
  path: string
  title: string
  name?: string
  component: HqRouteComponent
  permissions: PermissionRequirement
  scopeTypes?: OrganizationScopeType[]
  children?: RouteRecordRaw[]
  /** 保留旧直达路由，但不把尚未批准的页面展示为页内 tab。 */
  visibleInTabs?: boolean
}

interface HqSecondLevelRouteOptions {
  path: string
  title: string
  permissions: PermissionRequirement
  tabs: HqTabPage[]
  scopeTypes?: OrganizationScopeType[]
  /** 保留旧直达路由，但不展示在侧边栏。 */
  hidden?: boolean
  /** 单链折叠成一级菜单项时 sidebar 读组 meta,需在组上带 icon */
  svgIcon?: string
}

function createHqTabRoute(activeMenu: string, tab: HqTabPage): RouteRecordRaw {
  return {
    path: tab.path,
    redirect: `${tab.path}/index`,
    component: markRaw(LevelTwoRouter),
    meta: {
      title: tab.title,
      hidden: true,
      permissions: toPermissionArray(tab.permissions),
      hqOnly: true,
      activeMenu,
      ...(tab.scopeTypes ? { scopeTypes: tab.scopeTypes } : {}),
    },
    children: [
      {
        path: `${tab.path}/index`,
        component: tab.component,
        name: tab.name,
        meta: {
          title: tab.title,
          hidden: true,
          showTag: true,
          permissions: toPermissionArray(tab.permissions),
          hqOnly: true,
          activeMenu,
          ...(tab.scopeTypes ? { scopeTypes: tab.scopeTypes } : {}),
        },
      },
      ...(tab.children ?? []),
    ],
  }
}

function createHqSecondLevelRoute(options: HqSecondLevelRouteOptions): RouteRecordRaw {
  const tabRoutes = options.tabs.map(tab => createHqTabRoute(options.path, tab))
  const visibleTabRoutes = injectRouteList(tabRoutes.filter((_, index) => options.tabs[index]?.visibleInTabs !== false))
  for (const route of tabRoutes) {
    if (!visibleTabRoutes.includes(route)) route.props = { routeList: visibleTabRoutes }
  }
  return {
    path: options.path,
    redirect: visibleTabRoutes[0]?.path ?? tabRoutes[0]?.path,
    meta: {
      title: options.title,
      showTag: true,
      permissions: toPermissionArray(options.permissions),
      hqOnly: true,
      activeMenu: options.path,
      ...(options.hidden ? { hidden: true } : {}),
      ...(options.scopeTypes ? { scopeTypes: options.scopeTypes } : {}),
      ...(options.svgIcon ? { svgIcon: options.svgIcon } : {}),
    },
    children: tabRoutes,
  }
}

function createLegacyRedirectRoute(
  path: string,
  redirect: HqRouteRedirect,
  permissions: PermissionRequirement,
  scopeTypes?: OrganizationScopeType[],
): RouteRecordRaw {
  return {
    path,
    redirect,
    meta: {
      hidden: true,
      permissions: toPermissionArray(permissions),
      hqOnly: true,
      ...(scopeTypes ? { scopeTypes } : {}),
    },
  }
}

/** 占位二级组(结构先行;ComingSoon 从 route.meta.title 取名)。 */
function createPlaceholderGroup(
  menuPath: string,
  tabPath: string,
  title: string,
  name: string,
  permissions: PermissionRequirement = HQ_ACTION_PERMISSIONS.enter,
  hidden = false,
): RouteRecordRaw {
  return createHqSecondLevelRoute({
    path: menuPath,
    title,
    permissions,
    hidden,
    tabs: [{ path: tabPath, title, name, component: ComingSoon, permissions }],
  })
}

// ============================================================
// 一级 1:首页(总部经营驾驶舱)
// ============================================================

const homeRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.homeMenu,
    title: "首页",
    svgIcon: "nav-dashboard",
    permissions: HQ_ACTION_PERMISSIONS.enter,
    tabs: [
      {
        path: HQ_PATHS.homeTab,
        title: "首页",
        name: "HqBrandHome",
        component: BrandHome,
        permissions: HQ_ACTION_PERMISSIONS.enter,
      },
    ],
  }),
]

// ============================================================
// 一级 2:报表中心(首页 / 经营分析 / 营业·桌台·商品·收款·财务·运营报表 /
//        经营预警 / 自定义报表 / 总部数据大屏)
// ============================================================

const businessReportRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.reportHomeMenu,
    title: "报表首页",
    permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
    scopeTypes: AGGREGATE_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.reportHomeTab,
        title: "报表首页",
        name: "HqReportCenterHome",
        component: ReportCenterHome,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
    ],
  }),
  createHqSecondLevelRoute({
    // 经营分析:仅展示已批准的四个入口；其余已注册能力保留直达兼容。
    path: HQ_PATHS.businessStatisticsMenu,
    title: "经营分析",
    permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
    scopeTypes: AGGREGATE_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.businessOverviewTab,
        title: "营业概览",
        name: "HqBusinessOverview",
        component: BusinessOverview,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        // 经营目标(hq-operations-plan 阶段A);叶子路径沿用原独立二级时期
        path: HQ_PATHS.operationTargetsTab,
        title: "经营目标",
        name: "HqOperationTargets",
        component: OperationTargets,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      ...(HQ_FEATURE_READINESS.reportAnalysisRegistered
        ? [{
            path: HQ_PATHS.tableAnalysisTab,
            title: "桌台分析",
            name: "HqTableAnalysis",
            component: TableAnalysis,
            permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
            scopeTypes: AGGREGATE_SCOPE_TYPES,
            visibleInTabs: false,
          }]
        : []),
      {
        path: HQ_PATHS.productAnalysisTab,
        title: "商品分析",
        name: "HqProductAnalysis",
        component: ProductAnalysis,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.memberAnalysisTab,
        title: "会员分析",
        name: "HqMemberAnalysis",
        component: MemberAnalysis,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      ...(HQ_FEATURE_READINESS.paymentAnalysisRegistered
        ? [{
            path: HQ_PATHS.paymentAnalysisTab,
            title: "收款分析",
            name: "HqPaymentAnalysis",
            component: PaymentAnalysis,
            permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
            scopeTypes: AGGREGATE_SCOPE_TYPES,
            visibleInTabs: false,
          }]
        : []),
    ],
  }),
  createHqSecondLevelRoute({
    path: HQ_PATHS.handoverReportMenu,
    title: "交接班报表",
    permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
    scopeTypes: AGGREGATE_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.handoverSummaryTab,
        title: "汇总统计",
        name: "HqHandoverBusinessSummary",
        component: HqHandoverBusinessSummary,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.handoverDailyTab,
        title: "交接班日报",
        name: "HqHandoverBusinessDaily",
        component: HqHandoverBusinessDaily,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.handoverBillDetailTab,
        title: "账单明细",
        name: "HqHandoverBusinessBillDetail",
        component: HqHandoverBusinessBillDetail,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.handoverProductStatisticsTab,
        title: "商品统计",
        name: "HqHandoverBusinessProduct",
        component: HqHandoverBusinessProduct,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.handoverTableStatisticsTab,
        title: "桌台统计",
        name: "HqHandoverBusinessTable",
        component: HqHandoverBusinessTable,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
    ],
  }),
  createHqSecondLevelRoute({
    // 营业报表(总部跨店维度;结构先行,能力随后端聚合切片点亮)
    path: HQ_PATHS.operatingReportsMenu,
    title: "营业报表",
    permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
    scopeTypes: AGGREGATE_SCOPE_TYPES,
    tabs: [
      ...(HQ_FEATURE_READINESS.comingSoonRegistered
        ? [
            {
              path: HQ_PATHS.orderStatsTab,
              title: "订单统计",
              name: "HqOrderStats",
              component: ComingSoon,
              permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
              scopeTypes: AGGREGATE_SCOPE_TYPES,
              visibleInTabs: false,
            },
            {
              path: HQ_PATHS.revenueDiscountTab,
              title: "收入优惠",
              name: "HqRevenueDiscount",
              component: ComingSoon,
              permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
              scopeTypes: AGGREGATE_SCOPE_TYPES,
              visibleInTabs: false,
            },
          ]
        : []),
      {
        path: HQ_PATHS.indicatorCompareTab,
        title: "指标对比",
        name: "HqIndicatorCompare",
        component: IndicatorCompare,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.comprehensiveStatsTab,
        title: "营业统计",
        name: "HqComprehensiveStats",
        component: OperatingStatistics,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
    ],
  }),
  ...(HQ_FEATURE_READINESS.comingSoonRegistered
    ? [
        createPlaceholderGroup(HQ_PATHS.tableReportsMenu, HQ_PATHS.tableReportsTab, "桌台报表", "HqTableReports", HQ_ACTION_PERMISSIONS.viewBusinessReports, true),
        createPlaceholderGroup(HQ_PATHS.productReportsMenu, HQ_PATHS.productReportsTab, "商品报表", "HqProductReports", HQ_ACTION_PERMISSIONS.viewBusinessReports, true),
        createPlaceholderGroup(HQ_PATHS.paymentReportsMenu, HQ_PATHS.paymentReportsTab, "收款报表", "HqPaymentReports", HQ_ACTION_PERMISSIONS.viewBusinessReports, true),
        createPlaceholderGroup(HQ_PATHS.financeReportsMenu, HQ_PATHS.financeReportsTab, "财务报表", "HqFinanceReports", HQ_ACTION_PERMISSIONS.viewBusinessReports, true),
      ]
    : []),
  createHqSecondLevelRoute({
    // 运营报表:员工绩效(hq-operations-plan 阶段B-④;叶子路径不变)
    path: HQ_PATHS.staffPerformanceMenu,
    title: "运营报表",
    permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
    scopeTypes: AGGREGATE_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.staffPerformanceTab,
        title: "员工绩效",
        name: "HqStaffPerformance",
        component: StaffPerformance,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.storeSnapshotTab,
        title: "门店实况",
        name: "HqStoreSnapshot",
        component: StoreSnapshot,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
    ],
  }),
  createHqSecondLevelRoute({
    // 经营预警(hq-operations-plan 阶段C):五类经营异常;IA v2 归入报表中心
    path: HQ_PATHS.opsAlertsMenu,
    title: "经营预警",
    permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
    scopeTypes: AGGREGATE_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.opsAlertsOverviewTab,
        title: "预警总览",
        name: "HqOpsAlertsOverview",
        component: OpsAlertsOverview,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.opsAlertsListTab,
        title: "预警明细",
        name: "HqOpsAlertsList",
        component: OpsAlertsList,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.opsAlertsRulesTab,
        title: "预警规则",
        name: "HqOpsAlertsRules",
        component: OpsAlertsRules,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
    ],
  }),
  ...(HQ_FEATURE_READINESS.customReportsRegistered
    ? [createHqSecondLevelRoute({
        path: HQ_PATHS.customReportsMenu,
        title: "自定义报表",
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
        hidden: true,
        tabs: [
          {
            path: HQ_PATHS.customReportsTab,
            title: "自定义报表",
            name: "HqCustomReports",
            component: CustomReports,
            permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
            scopeTypes: AGGREGATE_SCOPE_TYPES,
          },
        ],
      })]
    : []),
  ...(HQ_FEATURE_READINESS.dataScreenRegistered
    ? [createHqSecondLevelRoute({
        path: HQ_PATHS.dataScreenMenu,
        title: "总部数据大屏",
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
        hidden: true,
        tabs: [
          {
            path: HQ_PATHS.dataScreenTab,
            title: "总部数据大屏",
            name: "HqDataScreen",
            component: DataScreen,
            permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
            scopeTypes: AGGREGATE_SCOPE_TYPES,
          },
        ],
      })]
    : []),
]

// ============================================================
// 一级 3:营销中心(首页 / 会员 / 活动 / 短信 / 报表 / 风险预警)
// ============================================================

const marketingHomeRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.marketingHomeMenu,
    title: "营销首页",
    permissions: HQ_ACTION_PERMISSIONS.viewMembers,
    tabs: [
      {
        // 会员经营驾驶舱(参照连锁餐饮营销中心首页);叶子路径不变
        path: HQ_PATHS.memberOverviewTab,
        title: "会员总览",
        name: "HqMemberOverview",
        component: MemberOverview,
        permissions: HQ_ACTION_PERMISSIONS.viewMembers,
      },
    ],
  }),
]

const memberGroupRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.membersMenu,
    title: "会员管理",
    permissions: mergePermissions(
      HQ_ACTION_PERMISSIONS.viewMembers,
      HQ_ACTION_PERMISSIONS.importMembers,
      HQ_ACTION_PERMISSIONS.migrateMembers,
      HQ_ACTION_PERMISSIONS.manageMemberDedupe,
      HQ_ACTION_PERMISSIONS.manageMemberLevels,
      ...(MEMBER_TAG_ROUTE_REGISTERED ? [CHAIN_ACTION_PERMISSIONS.manageMemberTags] : []),
    ),
    tabs: [
      {
        // 会员列表、详情和当前操作暂时统一复用会员资产查看权限，后续再细分按钮权限。
        path: HQ_PATHS.membersListTab,
        title: "会员列表",
        name: "HqMembers",
        component: Members,
        permissions: HQ_ACTION_PERMISSIONS.viewMembers,
        children: [
          {
            path: `${HQ_PATHS.membersListTab}/detail/:merchantCustomerUserId`,
            component: MemberDetail,
            name: "HqMemberDetail",
            meta: {
              title: "会员详情",
              hidden: true,
              activeMenu: HQ_PATHS.membersMenu,
              permissions: HQ_ACTION_PERMISSIONS.viewMembers,
              hqOnly: true,
            },
          },
        ],
      },
      {
        path: HQ_PATHS.memberImportTab,
        title: "会员导入",
        name: "HqMemberImport",
        component: MemberImport,
        permissions: HQ_ACTION_PERMISSIONS.importMembers,
      },
      {
        path: HQ_PATHS.memberLevelsTab,
        title: "等级与权益",
        name: "HqMemberLevels",
        component: MemberLevels,
        permissions: HQ_ACTION_PERMISSIONS.manageMemberLevels,
      },
      {
        path: HQ_PATHS.memberMigrationTab,
        title: "会员迁移",
        name: "HqMemberMigration",
        component: MemberMigration,
        permissions: HQ_ACTION_PERMISSIONS.migrateMembers,
      },
      {
        // 档案治理(增量去重):同手机号多份主档核对合并
        path: HQ_PATHS.memberDedupeTab,
        title: "档案治理",
        name: "HqMemberDedupe",
        component: MemberDedupe,
        permissions: HQ_ACTION_PERMISSIONS.manageMemberDedupe,
      },
      ...(MEMBER_TAG_ROUTE_REGISTERED ? [{
        // 会员标签(CRM 分群工具):系统自动标(RFM/生命周期派生)+ 手动标目录
        path: HQ_PATHS.memberTagsTab,
        title: "会员标签",
        name: "HqMemberTags",
        component: MemberTags,
        permissions: CHAIN_ACTION_PERMISSIONS.manageMemberTags,
      }] : []),
      ...(LOCAL_PREVIEW_ROUTES_ENABLED ? [{
        path: HQ_PATHS.crmInsightsTab,
        title: "会员洞察",
        name: "HqCrmInsights",
        component: CrmInsights,
        permissions: HQ_ACTION_PERMISSIONS.viewMembers,
      }] : []),
    ],
  }),
  createLegacyRedirectRoute(HQ_PATHS.memberLevelsMenu, HQ_PATHS.memberLevelsTab, HQ_ACTION_PERMISSIONS.manageMemberLevels),
]

const activityRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.storedValueMenu,
    title: "活动",
    permissions: mergePermissions(
      HQ_ACTION_PERMISSIONS.manageStoredValueRules,
      HQ_ACTION_PERMISSIONS.manageCashbackRules,
      ...(HQ_FEATURE_READINESS.referralCashbackRegistered ? [referralCashbackViewPermissions] : []),
      HQ_ACTION_PERMISSIONS.manageCouponTemplates,
      HQ_ACTION_PERMISSIONS.manageCouponRules,
      ...(LOCAL_PREVIEW_ROUTES_ENABLED ? [HQ_ACTION_PERMISSIONS.manageMessaging] : []),
    ),
    scopeTypes: HEADQUARTER_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.storedValueTab,
        title: "储值规则",
        name: "HqStoredValueRules",
        component: StoredValueRules,
        permissions: HQ_ACTION_PERMISSIONS.manageStoredValueRules,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.cashbackTab,
        title: "返现规则",
        name: "HqCashbackRules",
        component: CashbackRules,
        permissions: HQ_ACTION_PERMISSIONS.manageCashbackRules,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      ...(HQ_FEATURE_READINESS.referralCashbackRegistered
        ? [{
            path: HQ_PATHS.referralCashbackTab,
            title: "好友返现活动",
            name: "HqReferralCashbackCampaigns",
            component: ReferralCashbackCampaigns,
            permissions: referralCashbackViewPermissions,
            scopeTypes: HEADQUARTER_SCOPE_TYPES,
          }]
        : []),
      {
        path: HQ_PATHS.couponTemplatesTab,
        title: "券模板",
        name: "HqCouponTemplates",
        component: CouponTemplates,
        permissions: HQ_ACTION_PERMISSIONS.manageCouponTemplates,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.couponRulesTab,
        title: "发券规则",
        name: "HqCouponRules",
        component: CouponRules,
        permissions: HQ_ACTION_PERMISSIONS.manageCouponRules,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.manualBenefitsTab,
        title: "手动发权益",
        name: "HqManualBenefits",
        component: ManualBenefits,
        permissions: HQ_ACTION_PERMISSIONS.manageCouponRules,
      },
      ...(LOCAL_PREVIEW_ROUTES_ENABLED
        ? [
            {
              path: HQ_PATHS.memberSegmentsTab,
              title: "圈人分群",
              name: "HqMemberSegments",
              component: MemberSegments,
              permissions: HQ_ACTION_PERMISSIONS.manageMessaging,
              scopeTypes: HEADQUARTER_SCOPE_TYPES,
            },
            {
              path: HQ_PATHS.overrideAuthorizationTab,
              title: "门店授权",
              name: "HqOverrideAuthorization",
              component: OverrideAuthorization,
              permissions: HQ_ACTION_PERMISSIONS.manageStoredValueRules,
              scopeTypes: HEADQUARTER_SCOPE_TYPES,
            },
          ]
        : []),
    ],
  }),
  createLegacyRedirectRoute(HQ_PATHS.cashbackMenu, HQ_PATHS.cashbackTab, HQ_ACTION_PERMISSIONS.manageCashbackRules, HEADQUARTER_SCOPE_TYPES),
  ...(HQ_FEATURE_READINESS.referralCashbackRegistered
    ? [createLegacyRedirectRoute(HQ_PATHS.referralCashbackMenu, HQ_PATHS.referralCashbackTab, referralCashbackViewPermissions, HEADQUARTER_SCOPE_TYPES)]
    : []),
  createLegacyRedirectRoute(HQ_PATHS.couponTemplatesMenu, HQ_PATHS.couponTemplatesTab, HQ_ACTION_PERMISSIONS.manageCouponTemplates, HEADQUARTER_SCOPE_TYPES),
  createLegacyRedirectRoute(HQ_PATHS.couponRulesMenu, HQ_PATHS.couponRulesTab, HQ_ACTION_PERMISSIONS.manageCouponRules, HEADQUARTER_SCOPE_TYPES),
  createLegacyRedirectRoute(HQ_PATHS.manualBenefitsMenu, HQ_PATHS.manualBenefitsTab, HQ_ACTION_PERMISSIONS.manageCouponRules),
]

const brandPointRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.brandPointsMenu,
    title: "积分管理",
    permissions: mergePermissions(
      HQ_ACTION_PERMISSIONS.manageBrandPointRules,
      HQ_ACTION_PERMISSIONS.manageBrandPointDeductionRules,
      HQ_ACTION_PERMISSIONS.previewBrandPointMigration,
      HQ_ACTION_PERMISSIONS.viewMemberAssets,
      HQ_ACTION_PERMISSIONS.viewBusinessReports,
    ),
    scopeTypes: HEADQUARTER_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.brandPointRulesTab,
        title: "积分规则",
        name: "HqBrandPointRules",
        component: BrandPointRules,
        permissions: HQ_ACTION_PERMISSIONS.viewMemberAssets,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.brandPointMigrationPreviewTab,
        title: "迁移预检",
        name: "HqBrandPointMigrationPreview",
        component: BrandPointMigrationPreview,
        permissions: HQ_ACTION_PERMISSIONS.previewBrandPointMigration,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.brandPointRecoveriesTab,
        title: "追偿明细",
        name: "HqBrandPointRecoveries",
        component: BrandPointRecoveries,
        permissions: HQ_ACTION_PERMISSIONS.viewMemberAssets,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.brandPointReportsTab,
        title: "积分报表",
        name: "HqBrandPointReports",
        component: BrandPointReports,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.brandPointMallTab,
        title: "积分商城",
        name: "HqBrandPointMall",
        component: BrandPointMall,
        permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
    ],
  }),
]

// 短信触达(占位;触达链路随触达中心切片点亮。路径避开 "messaging" 命名收敛约束)
const smsRoutes: RouteRecordRaw[] = [
  ...(HQ_FEATURE_READINESS.comingSoonRegistered
    ? [createPlaceholderGroup(HQ_PATHS.marketingSmsMenu, HQ_PATHS.marketingSmsTab, "短信", "HqMarketingSms", HQ_ACTION_PERMISSIONS.viewMembers)]
    : []),
]

const messagingPreviewRoutes: RouteRecordRaw[] = [
  ...(LOCAL_PREVIEW_ROUTES_ENABLED
    ? [createHqSecondLevelRoute({
        path: HQ_PATHS.messagingMenu,
        title: "触达中心",
        permissions: HQ_ACTION_PERMISSIONS.manageMessaging,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
        tabs: [
          {
            path: HQ_PATHS.messagingTab,
            title: "批量触达",
            name: "HqMessagingCampaigns",
            component: MessagingCampaigns,
            permissions: HQ_ACTION_PERMISSIONS.manageMessaging,
            scopeTypes: HEADQUARTER_SCOPE_TYPES,
          },
        ],
      })]
    : []),
]

const marketingReportRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.memberAssetsMenu,
    title: "报表",
    permissions: mergePermissions(
      HQ_ACTION_PERMISSIONS.viewMemberAssets,
      HQ_ACTION_PERMISSIONS.manageOrders,
    ),
    tabs: [
      {
        path: HQ_PATHS.memberAssetsTab,
        title: "资产看板",
        name: "HqMemberAssets",
        component: MemberAssets,
        permissions: HQ_ACTION_PERMISSIONS.viewMemberAssets,
      },
      {
        path: HQ_PATHS.ordersTab,
        title: "订单管理",
        name: "HqOrders",
        component: Orders,
        permissions: HQ_ACTION_PERMISSIONS.manageOrders,
      },
      ...(HQ_FEATURE_READINESS.marketingEffectRegistered
        ? [{
            path: HQ_PATHS.marketingEffectTab,
            title: "营销效果",
            name: "HqMarketingEffect",
            component: MarketingEffect,
            permissions: HQ_ACTION_PERMISSIONS.viewMemberAssets,
          }]
        : []),
      ...(LOCAL_PREVIEW_ROUTES_ENABLED ? [{
        path: HQ_PATHS.assetPolicyTab,
        title: "资产策略",
        name: "HqAssetPolicy",
        component: AssetPolicy,
        permissions: HQ_ACTION_PERMISSIONS.viewMemberAssets,
      }] : []),
    ],
  }),
  createLegacyRedirectRoute(HQ_PATHS.ordersMenu, HQ_PATHS.ordersTab, HQ_ACTION_PERMISSIONS.manageOrders),
]

function createMemberRiskRoutes(): RouteRecordRaw[] {
  if (!MEMBER_RISK_AVAILABLE) return []
  return [createHqSecondLevelRoute({
    path: HQ_PATHS.riskWarningMenu,
    title: "会员风险预警",
    permissions: HQ_ACTION_PERMISSIONS.viewMembers,
    tabs: [
      {
        path: HQ_PATHS.riskOverviewTab,
        title: "风险总览",
        name: "HqMemberRiskOverview",
        component: () => import("@/views/page/member-risk/hq/overview/index.vue"),
        permissions: HQ_ACTION_PERMISSIONS.viewMembers,
      },
      {
        path: HQ_PATHS.riskCustomerListTab,
        title: "风险会员名单",
        name: "HqMemberRiskCustomerList",
        component: () => import("@/views/page/member-risk/hq/customer-list/index.vue"),
        permissions: HQ_ACTION_PERMISSIONS.viewMembers,
      },
      {
        path: HQ_PATHS.riskRuleConfigTab,
        title: "识别规则配置",
        name: "HqMemberRiskRuleConfig",
        component: () => import("@/views/page/member-risk/hq/rule-config/index.vue"),
        permissions: HQ_ACTION_PERMISSIONS.viewMembers,
      },
    ],
  })]
}

const memberRiskRoutes: RouteRecordRaw[] = createMemberRiskRoutes()

// ============================================================
// 一级 4:预订中心(预订看板 / 预订配置)
// ============================================================

const reservationCenterRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.reservationDashboardMenu,
    title: "预订看板",
    permissions: HQ_ACTION_PERMISSIONS.manageOrders,
    scopeTypes: HEADQUARTER_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.reservationSummaryTab,
        title: "汇总统计",
        name: "HqReservationDashboardSummary",
        component: ReservationDashboardSummary,
        permissions: HQ_ACTION_PERMISSIONS.manageOrders,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.reservationOrderDetailsTab,
        title: "预订明细",
        name: "HqReservationOrderDetails",
        component: ReservationOrderDetails,
        permissions: HQ_ACTION_PERMISSIONS.manageOrders,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
    ],
  }),

]

// ============================================================
// 一级 5:采购中心(供应商管理 / 效期管理)
// ============================================================

const procurementRoutes: RouteRecordRaw[] = [
  ...(HQ_FEATURE_READINESS.comingSoonRegistered
    ? [
        createPlaceholderGroup(HQ_PATHS.supplierMenu, HQ_PATHS.supplierTab, "供应商管理", "HqSuppliers"),
        createPlaceholderGroup(HQ_PATHS.expiryMenu, HQ_PATHS.expiryTab, "效期管理", "HqExpiry"),
      ]
    : []),
]

const financeRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.memberStoredValueMenu,
    title: "会员储值",
    permissions: storedValueReportPermissions,
    scopeTypes: AGGREGATE_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.memberStoredValueRechargeConsumptionStatisticsTab,
        title: "充消统计",
        name: "HqStoredValueRechargeConsumptionStatistics",
        component: StoredValueRechargeConsumptionStatistics,
        permissions: storedValueReportPermissions,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.memberStoredValueLedgerTab,
        title: "会员储值流水",
        name: "HqStoredValueMemberLedger",
        component: StoredValueMemberLedger,
        permissions: storedValueReportPermissions,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.memberStoredValueConsumptionTraceTab,
        title: "消费溯源",
        name: "HqStoredValueConsumptionTrace",
        component: StoredValueConsumptionTrace,
        permissions: storedValueReportPermissions,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.memberStoredValueSettlementTab,
        title: "跨店消费明细",
        name: "HqSettlementLedger",
        component: CrossStoreConsumptionDetail,
        permissions: storedValueReportPermissions,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
    ],
  }),
  // 已交付路由始终进入 production bundle；权限与版本路由分别控制访问和环境流量。
  createHqSecondLevelRoute({
    path: HQ_PATHS.incomeExpenseMenu,
    title: "收支管理",
    permissions: financePermissions,
    scopeTypes: AGGREGATE_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.incomeExpenseOverviewTab,
        title: "经营概览",
        name: "HqFinanceIncomeExpenseOverview",
        component: IncomeExpenseOverview,
        permissions: financePermissions,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.incomeExpenseMonthlyTab,
        title: "月结费用",
        name: "HqFinanceMonthlyExpenses",
        component: MonthlyExpenses,
        permissions: financePermissions,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.incomeExpenseLedgerTab,
        title: "账目明细",
        name: "HqFinanceLedger",
        component: IncomeExpenseLedger,
        permissions: financePermissions,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.incomeExpenseCategoriesTab,
        title: "类型设置",
        name: "HqFinanceExpenseCategories",
        component: ExpenseCategories,
        permissions: financePermissions,
        scopeTypes: AGGREGATE_SCOPE_TYPES,
      },
    ],
  }),
]

export function resolveFinanceDefaultRedirect() {
  return HQ_PATHS.memberStoredValueRechargeConsumptionStatisticsTab
}

const financeDefaultRedirect = resolveFinanceDefaultRedirect()

// ============================================================
// 一级 7:运营中心(门店管理 / 商品管理 / 预订管理 /
//        品牌小程序管理 / 组织机构与账号)
// ============================================================

const operationsCenterRoutes: RouteRecordRaw[] = [
  createHqSecondLevelRoute({
    path: HQ_PATHS.storesMenu,
    title: "门店管理",
    permissions: HQ_ACTION_PERMISSIONS.viewStores,
    tabs: [
      {
        path: HQ_PATHS.storesTab,
        title: "门店管理",
        name: "HqStores",
        component: Stores,
        permissions: HQ_ACTION_PERMISSIONS.viewStores,
      },
    ],
  }),
  ...(LOCAL_PREVIEW_ROUTES_ENABLED
    ? [createHqSecondLevelRoute({
        path: HQ_PATHS.storeGroupsMenu,
        title: "门店分组",
        permissions: HQ_ACTION_PERMISSIONS.viewStores,
        tabs: [
          {
            path: HQ_PATHS.storeGroupsTab,
            title: "门店分组",
            name: "HqStoreGroups",
            component: StoreGroups,
            permissions: HQ_ACTION_PERMISSIONS.viewStores,
          },
        ],
      })]
    : []),
  ...(HQ_FEATURE_READINESS.comingSoonRegistered
    ? [createPlaceholderGroup(HQ_PATHS.productManageMenu, HQ_PATHS.productManageTab, "商品管理", "HqProductManage")]
    : []),
  ...(HQ_FEATURE_READINESS.reservationRulesRegistered
    ? [createHqSecondLevelRoute({
        path: HQ_PATHS.reservationManageMenu,
        title: "预订管理",
        permissions: HQ_ACTION_PERMISSIONS.manageReservationRules,
        tabs: [
          {
            path: HQ_PATHS.reservationRulesTab,
            title: "预订规则",
            name: "HqReservationRules",
            component: ReservationRules,
            permissions: HQ_ACTION_PERMISSIONS.manageReservationRules,
          },
        ],
      })]
    : []),
  createHqSecondLevelRoute({
    path: HQ_PATHS.brandFacadeMenu,
    // "品牌小程序管理"7 字超侧栏宽度(2026-07-07 实测溢出),沿用 5 字缩名
    title: "品牌小程序",
    permissions: HQ_ACTION_PERMISSIONS.manageBrandFacade,
    scopeTypes: HEADQUARTER_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.brandFacadeTab,
        title: "品牌门面",
        name: "HqBrandFacade",
        component: BrandFacade,
        permissions: HQ_ACTION_PERMISSIONS.manageBrandFacade,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
    ],
  }),
  createHqSecondLevelRoute({
    path: HQ_PATHS.employeeBindingsMenu,
    // "组织机构与账号"7 字超侧栏宽度,沿用 5 字缩名(组织=区域,账号=员工绑定)
    title: "组织与账号",
    permissions: mergePermissions(
      HQ_ACTION_PERMISSIONS.manageRegions,
      HQ_ACTION_PERMISSIONS.manageChainEmployeeBindings,
    ),
    tabs: [
      {
        path: HQ_PATHS.regionsTab,
        title: "区域管理",
        name: "HqRegions",
        component: Regions,
        permissions: HQ_ACTION_PERMISSIONS.manageRegions,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.employeeBindingsTab,
        title: "员工绑定",
        name: "HqChainEmployeeBindings",
        component: ChainEmployeeBindings,
        permissions: HQ_ACTION_PERMISSIONS.manageChainEmployeeBindings,
      },
    ],
  }),
  createHqSecondLevelRoute({
    path: HQ_PATHS.productInventoryMenu,
    title: "总部商品",
    permissions: HQ_ACTION_PERMISSIONS.enter,
    scopeTypes: HEADQUARTER_SCOPE_TYPES,
    tabs: [
      {
        path: HQ_PATHS.productOverviewTab,
        title: "经营总览",
        name: "HqProductOverview",
        component: ProductInventory,
        permissions: HQ_ACTION_PERMISSIONS.enter,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.headquartersProductsTab,
        title: "总部商品",
        name: "HqHeadquartersProducts",
        component: ProductInventory,
        permissions: HQ_ACTION_PERMISSIONS.enter,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
      {
        path: HQ_PATHS.headquartersProductAnalysisTab,
        title: "销售统计",
        name: "HqHeadquartersProductAnalysis",
        component: ProductInventory,
        permissions: HQ_ACTION_PERMISSIONS.enter,
        scopeTypes: HEADQUARTER_SCOPE_TYPES,
      },
    ],
  }),
]

// ============================================================
// 旧路径 legacy redirects(IA v2 迁移兜底;叶子路径未变,仅旧组/一级前缀)
// ============================================================

const legacyHqRedirectRoutes: RouteRecordRaw[] = [
  createLegacyRedirectRoute(
    HQ_PATHS.productInventoryLegacyTab,
    HQ_PATHS.productOverviewTab,
    HQ_ACTION_PERMISSIONS.enter,
    HEADQUARTER_SCOPE_TYPES,
  ),
  createLegacyRedirectRoute(
    `${HQ_PATHS.productInventoryLegacyTab}/index`,
    `${HQ_PATHS.productOverviewTab}/index`,
    HQ_ACTION_PERMISSIONS.enter,
    HEADQUARTER_SCOPE_TYPES,
  ),
  createLegacyRedirectRoute("brand-facade", HQ_PATHS.brandFacadeTab, HQ_ACTION_PERMISSIONS.manageBrandFacade, HEADQUARTER_SCOPE_TYPES),
  createLegacyRedirectRoute("stores", HQ_PATHS.storesTab, HQ_ACTION_PERMISSIONS.viewStores),
  createLegacyRedirectRoute("employee-bindings", HQ_PATHS.employeeBindingsTab, HQ_ACTION_PERMISSIONS.manageChainEmployeeBindings),
  createLegacyRedirectRoute("member-migration", HQ_PATHS.memberMigrationTab, HQ_ACTION_PERMISSIONS.migrateMembers),
  createLegacyRedirectRoute("members", HQ_PATHS.membersListTab, HQ_ACTION_PERMISSIONS.viewMembers),
  createLegacyRedirectRoute(
    "members/detail/:merchantCustomerUserId",
    to => ({
      name: "HqMemberDetail",
      params: to.params,
      query: to.query,
    }),
    HQ_ACTION_PERMISSIONS.viewMembers,
  ),
  createLegacyRedirectRoute("member-levels", HQ_PATHS.memberLevelsTab, HQ_ACTION_PERMISSIONS.manageMemberLevels),
  createLegacyRedirectRoute("stored-value-rules", HQ_PATHS.storedValueTab, HQ_ACTION_PERMISSIONS.manageStoredValueRules),
  createLegacyRedirectRoute("cashback-rules", HQ_PATHS.cashbackTab, HQ_ACTION_PERMISSIONS.manageCashbackRules),
  createLegacyRedirectRoute("coupon-templates", HQ_PATHS.couponTemplatesTab, HQ_ACTION_PERMISSIONS.manageCouponTemplates),
  createLegacyRedirectRoute("coupon-rules", HQ_PATHS.couponRulesTab, HQ_ACTION_PERMISSIONS.manageCouponRules),
  createLegacyRedirectRoute("manual-benefits", HQ_PATHS.manualBenefitsTab, HQ_ACTION_PERMISSIONS.manageCouponRules),
  createLegacyRedirectRoute("orders", HQ_PATHS.ordersTab, HQ_ACTION_PERMISSIONS.manageOrders),
  ...(HQ_FEATURE_READINESS.reservationRulesRegistered
    ? [createLegacyRedirectRoute("reservation-rules", HQ_PATHS.reservationRulesTab, HQ_ACTION_PERMISSIONS.manageReservationRules)]
    : []),
  // 2026-07-07 菜单重构遗留兜底
  createLegacyRedirectRoute("/hq/brand-building/stores/stores/index", HQ_PATHS.storesTab, HQ_ACTION_PERMISSIONS.viewStores),
  createLegacyRedirectRoute("/hq/brand-building/stores", HQ_PATHS.storesTab, HQ_ACTION_PERMISSIONS.viewStores),
  createLegacyRedirectRoute("/hq/brand-building/employee-bindings/employee-bindings/index", HQ_PATHS.employeeBindingsTab, HQ_ACTION_PERMISSIONS.manageChainEmployeeBindings),
  createLegacyRedirectRoute("/hq/brand-building/employee-bindings", HQ_PATHS.employeeBindingsTab, HQ_ACTION_PERMISSIONS.manageChainEmployeeBindings),
  createLegacyRedirectRoute(
    HQ_PATHS.memberStoredValueRechargeStatisticsTab,
    HQ_PATHS.memberStoredValueRechargeConsumptionStatisticsTab,
    storedValueReportPermissions,
    AGGREGATE_SCOPE_TYPES,
  ),
  createLegacyRedirectRoute(
    `${HQ_PATHS.memberStoredValueRechargeStatisticsTab}/index`,
    HQ_PATHS.memberStoredValueRechargeConsumptionStatisticsTab,
    storedValueReportPermissions,
    AGGREGATE_SCOPE_TYPES,
  ),
  createLegacyRedirectRoute(
    HQ_PATHS.memberStoredValueConsumptionStatisticsTab,
    HQ_PATHS.memberStoredValueRechargeConsumptionStatisticsTab,
    storedValueReportPermissions,
    AGGREGATE_SCOPE_TYPES,
  ),
  createLegacyRedirectRoute(
    `${HQ_PATHS.memberStoredValueConsumptionStatisticsTab}/index`,
    HQ_PATHS.memberStoredValueRechargeConsumptionStatisticsTab,
    storedValueReportPermissions,
    AGGREGATE_SCOPE_TYPES,
  ),
  ...(financeRoutes.length > 0
    ? [
        createLegacyRedirectRoute(
          "/hq/member-assets/settlement/settlement/index",
          HQ_PATHS.memberStoredValueSettlementTab,
          storedValueReportPermissions,
          AGGREGATE_SCOPE_TYPES,
        ),
        createLegacyRedirectRoute(
          "/hq/member-assets/settlement",
          HQ_PATHS.memberStoredValueSettlementTab,
          storedValueReportPermissions,
          AGGREGATE_SCOPE_TYPES,
        ),
        createLegacyRedirectRoute(
          "/hq/finance/settlement",
          HQ_PATHS.memberStoredValueSettlementTab,
          storedValueReportPermissions,
          AGGREGATE_SCOPE_TYPES,
        ),
        createLegacyRedirectRoute(
          "/hq/finance/settlement/settlement",
          HQ_PATHS.memberStoredValueSettlementTab,
          storedValueReportPermissions,
          AGGREGATE_SCOPE_TYPES,
        ),
        createLegacyRedirectRoute(
          "/hq/finance/settlement/settlement/index",
          HQ_PATHS.memberStoredValueSettlementTab,
          storedValueReportPermissions,
          AGGREGATE_SCOPE_TYPES,
        ),
      ]
    : []),
  // IA v2 迁移兜底:旧一级/旧组前缀 → 新落点
  ...(HQ_FEATURE_READINESS.reservationCenterRegistered
    ? [
        createLegacyRedirectRoute(
          "/hq/reservation-center/main",
          HQ_PATHS.reservationDashboardMenu,
          HQ_ACTION_PERMISSIONS.manageOrders,
          HEADQUARTER_SCOPE_TYPES,
        ),
        createLegacyRedirectRoute(
          "/hq/reservation-center/main/index",
          `${HQ_PATHS.reservationSummaryTab}/index`,
          HQ_ACTION_PERMISSIONS.manageOrders,
          HEADQUARTER_SCOPE_TYPES,
        ),
        createLegacyRedirectRoute(
          "/hq/reservation-center/main/index/index",
          `${HQ_PATHS.reservationSummaryTab}/index`,
          HQ_ACTION_PERMISSIONS.manageOrders,
          HEADQUARTER_SCOPE_TYPES,
        ),
        ...[
          HQ_PATHS.reservationConfigMenu,
          HQ_PATHS.reservationBasicRulesTab,
          `${HQ_PATHS.reservationBasicRulesTab}/index`,
          HQ_PATHS.reservationRoomSettingsTab,
          `${HQ_PATHS.reservationRoomSettingsTab}/index`,
          "/hq/reservation-center/main/room-floor-plan",
          "/hq/reservation-center/main/room-floor-plan/index",
          "/hq/reservation-center/settings",
          "/hq/reservation-center/settings/basic-rules",
          "/hq/reservation-center/settings/basic-rules/index",
          "/hq/reservation-center/rooms",
          "/hq/reservation-center/rooms/floor-plan",
          "/hq/reservation-center/rooms/floor-plan/index",
        ].map(path => createLegacyRedirectRoute(
          path,
          HQ_PATHS.reservationSummaryTab,
          HQ_ACTION_PERMISSIONS.manageOrders,
          HEADQUARTER_SCOPE_TYPES,
        )),
      ]
    : []),
  ...(HQ_FEATURE_READINESS.comingSoonRegistered
    ? [
        createLegacyRedirectRoute("/hq/procurement-center/main", HQ_PATHS.supplierTab, HQ_ACTION_PERMISSIONS.enter),
        createLegacyRedirectRoute("/hq/procurement-center/main/index", HQ_PATHS.supplierTab, HQ_ACTION_PERMISSIONS.enter),
      ]
    : []),
  createLegacyRedirectRoute("/hq/organization", HQ_PATHS.storesTab, HQ_ACTION_PERMISSIONS.viewStores),
  createLegacyRedirectRoute("/hq/organization/regions", HQ_PATHS.regionsTab, HQ_ACTION_PERMISSIONS.manageRegions, HEADQUARTER_SCOPE_TYPES),
  createLegacyRedirectRoute("/hq/ops-alerts", HQ_PATHS.opsAlertsOverviewTab, HQ_ACTION_PERMISSIONS.viewBusinessReports),
  createLegacyRedirectRoute("/hq/brand-building", HQ_PATHS.brandFacadeTab, HQ_ACTION_PERMISSIONS.manageBrandFacade, HEADQUARTER_SCOPE_TYPES),
  createLegacyRedirectRoute(
    "/hq/member-marketing",
    HQ_PATHS.storedValueMenu,
    mergePermissions(
      HQ_ACTION_PERMISSIONS.manageStoredValueRules,
      HQ_ACTION_PERMISSIONS.manageCashbackRules,
      HQ_ACTION_PERMISSIONS.manageCouponTemplates,
      HQ_ACTION_PERMISSIONS.manageCouponRules,
    ),
    HEADQUARTER_SCOPE_TYPES,
  ),
  createLegacyRedirectRoute(
    "/hq/member-assets",
    HQ_PATHS.memberAssetsMenu,
    mergePermissions(HQ_ACTION_PERMISSIONS.viewMemberAssets, HQ_ACTION_PERMISSIONS.manageOrders),
  ),
]

// ============================================================
// 总部路由树(IA v2:六大一级)
// ============================================================

const hq: RouteRecordRaw[] = [
  {
    path: "/hq",
    component: Layout,
    redirect: HQ_PATHS.homeTab,
    name: "HqRoot",
    meta: {
      order: 6,
      title: "总部",
      svgIcon: "nav-hq",
      permissions: mergePermissions(
        HQ_ROOT_ACTION_PERMISSIONS,
        ...(MEMBER_TAG_ROUTE_REGISTERED ? [CHAIN_ACTION_PERMISSIONS.manageMemberTags] : []),
      ),
      hqOnly: true,
      scopeTypes: AGGREGATE_SCOPE_TYPES,
      flattenInSidebar: true,
    },
    children: [
      {
        path: "/hq/home",
        redirect: HQ_PATHS.homeTab,
        meta: {
          title: "首页",
          svgIcon: "nav-dashboard",
          permissions: HQ_ACTION_PERMISSIONS.enter,
          hqOnly: true,
        },
        children: homeRoutes,
      },
      {
        path: "/hq/business-reports",
        redirect: HQ_PATHS.reportHomeTab,
        meta: {
          title: "报表中心",
          svgIcon: "nav-business",
          permissions: HQ_ACTION_PERMISSIONS.viewBusinessReports,
          capabilityCode: "business_reports",
          alwaysShow: true,
          hqOnly: true,
          scopeTypes: AGGREGATE_SCOPE_TYPES,
        },
        children: businessReportRoutes,
      },
      {
        path: "/hq/member-management",
        redirect: HQ_PATHS.memberOverviewTab,
        meta: {
          title: "营销中心",
          svgIcon: "nav-member",
          permissions: mergePermissions(
            HQ_ACTION_PERMISSIONS.viewMembers,
            HQ_ACTION_PERMISSIONS.importMembers,
            HQ_ACTION_PERMISSIONS.migrateMembers,
            HQ_ACTION_PERMISSIONS.manageMemberDedupe,
            HQ_ACTION_PERMISSIONS.manageMemberLevels,
            ...(MEMBER_TAG_ROUTE_REGISTERED ? [CHAIN_ACTION_PERMISSIONS.manageMemberTags] : []),
            HQ_ACTION_PERMISSIONS.manageStoredValueRules,
            HQ_ACTION_PERMISSIONS.manageCashbackRules,
            HQ_ACTION_PERMISSIONS.manageCouponTemplates,
            HQ_ACTION_PERMISSIONS.manageCouponRules,
            ...(LOCAL_PREVIEW_ROUTES_ENABLED ? [HQ_ACTION_PERMISSIONS.manageMessaging] : []),
            HQ_ACTION_PERMISSIONS.manageBrandPointRules,
            HQ_ACTION_PERMISSIONS.adjustBrandPoints,
            HQ_ACTION_PERMISSIONS.previewBrandPointMigration,
            HQ_ACTION_PERMISSIONS.viewMemberAssets,
            HQ_ACTION_PERMISSIONS.viewBusinessReports,
            HQ_ACTION_PERMISSIONS.manageOrders,
          ),
          alwaysShow: true,
          hqOnly: true,
        },
        children: [
          ...marketingHomeRoutes,
          ...memberGroupRoutes,
          ...activityRoutes,
          ...messagingPreviewRoutes,
          ...brandPointRoutes,
          ...smsRoutes,
          ...marketingReportRoutes,
          ...memberRiskRoutes,
        ],
      },
      ...(financeRoutes.length > 0
        ? [{
            path: "/hq/finance",
            redirect: financeDefaultRedirect ?? financeRoutes[0]!.path,
            meta: {
              title: "财务中心",
              svgIcon: "nav-bookkeeping",
              permissions: financeRootPermissions,
              alwaysShow: true,
              hqOnly: true,
              scopeTypes: AGGREGATE_SCOPE_TYPES,
            },
            children: financeRoutes,
          }]
        : []),
      ...(HQ_FEATURE_READINESS.reservationCenterRegistered
        ? [{
            path: "/hq/reservation-center",
            redirect: HQ_PATHS.reservationSummaryTab,
            meta: {
              title: "预订中心",
              svgIcon: "nav-order",
              permissions: HQ_ACTION_PERMISSIONS.manageOrders,
              alwaysShow: true,
              hqOnly: true,
              scopeTypes: HEADQUARTER_SCOPE_TYPES,
            },
            children: reservationCenterRoutes,
          }]
        : []),
      ...(HQ_FEATURE_READINESS.comingSoonRegistered
        ? [{
            path: "/hq/procurement-center",
            redirect: HQ_PATHS.supplierTab,
            meta: {
              title: "采购中心",
              svgIcon: "nav-procurement",
              permissions: HQ_ACTION_PERMISSIONS.enter,
              alwaysShow: true,
              hqOnly: true,
            },
            children: procurementRoutes,
          }]
        : []),
      {
        path: "/hq/operations-center",
        redirect: HQ_PATHS.storesTab,
        meta: {
          title: "运营中心",
          svgIcon: "nav-setting",
          permissions: mergePermissions(
            HQ_ACTION_PERMISSIONS.enter,
            HQ_ACTION_PERMISSIONS.viewStores,
            HQ_ACTION_PERMISSIONS.manageRegions,
            HQ_ACTION_PERMISSIONS.manageChainEmployeeBindings,
            ...(HQ_FEATURE_READINESS.reservationRulesRegistered
              ? [HQ_ACTION_PERMISSIONS.manageReservationRules]
              : []),
            HQ_ACTION_PERMISSIONS.manageBrandFacade,
          ),
          alwaysShow: true,
          hqOnly: true,
        },
        children: operationsCenterRoutes,
      },
      ...legacyHqRedirectRoutes,
    ],
  },
]

export default hq
